/**
 * DashboardPage — метрики активности и качества Anima.
 *
 * Phase 6 Étap 4: Dashboard
 */

import { Link } from 'react-router-dom';
import { Card, Spinner, Badge, Button, OnboardingHint } from '../components/ui';
import { useDashboardMetrics, type DailyActivity, type ChannelStats } from '../api/dashboard';

// ============================================================================
// DashboardPage
// ============================================================================

export function DashboardPage(): JSX.Element {
  const { data: metrics, isLoading, error } = useDashboardMetrics();

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Spinner size="lg" className="text-accent-brand" />
      </div>
    );
  }

  if (error || !metrics) {
    return (
      <div className="h-full flex items-center justify-center text-text-secondary">
        <p>Не удалось загрузить метрики</p>
      </div>
    );
  }

  // Empty state — ещё нет данных
  if (metrics.summary.totalProposals === 0 && metrics.channels.length === 0) {
    return (
      <div className="h-full overflow-y-auto bg-surface-base">
        <div className="max-w-5xl mx-auto px-6 py-8">
          <h1 className="font-heading text-2xl font-bold text-text-primary">Дашборд</h1>
          <p className="text-sm text-text-secondary mt-1 mb-12">Обзор активности и качества Anima</p>
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-20 h-20 rounded-full bg-surface-subtle border border-border-subtle flex items-center justify-center mb-6">
              <svg className="w-10 h-10 text-text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
              </svg>
            </div>
            <h3 className="font-heading text-xl font-semibold text-text-primary mb-2">
              Anima ещё не обработала сообщений
            </h3>
            <p className="text-text-secondary text-center max-w-sm mb-6">
              Подключите канал и получите первые сообщения — метрики появятся автоматически
            </p>
            <Link to="/channels">
              <Button variant="primary" size="md">Подключить канал</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-surface-base">
      <div className="max-w-5xl mx-auto px-6 py-8 space-y-8">
        {/* Header */}
        <div>
          <h1 className="font-heading text-2xl font-bold text-text-primary">Дашборд</h1>
          <p className="text-sm text-text-secondary mt-1">
            Обзор активности и качества Anima.{' '}
            {/* Числа здесь по всей организации, а инбокс менеджера — только
                его диалоги. Без этой подписи менеджер видит «26 черновиков»
                при трёх своих и считает, что интерфейс врёт */}
            <span className="text-text-muted">Числа — по всей организации, не только по вашим диалогам.</span>
          </p>
        </div>

        <OnboardingHint hintId="dashboard-intro" icon="📊">
          Здесь отображается статистика работы Anima: сколько черновиков сгенерировано,
          сколько принято операторами, и как распределяется нагрузка по каналам.
          Чем больше операторы используют Inbox — тем точнее становятся предложения.
        </OnboardingHint>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            label="Proposals"
            value={metrics.summary.totalProposals}
            subtitle="всего сгенерировано"
          />
          <MetricCard
            label="Принято"
            value={metrics.summary.acceptedProposals}
            subtitle={`${metrics.summary.acceptRate}% accept rate`}
            accent
          />
          <MetricCard
            label="Активные диалоги"
            value={metrics.summary.activeDialogues}
            subtitle="прямо сейчас"
          />
          <MetricCard
            label="Match Score"
            value={metrics.summary.avgMatchScore !== null
              ? `${(metrics.summary.avgMatchScore * 100).toFixed(0)}%`
              : '—'}
            subtitle="средний по всем"
          />
        </div>

        {/* Activity Chart */}
        <Card className="p-0">
          <div className="px-5 pt-5 pb-2">
            <h2 className="font-heading font-semibold text-text-primary">Активность за 14 дней</h2>
          </div>
          <div className="px-5 pb-5">
            <ActivityChart activity={metrics.activity} />
          </div>
        </Card>

        {/* Channel Breakdown */}
        <div>
          <h2 className="font-heading font-semibold text-text-primary mb-4">По каналам</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {metrics.channels.map(ch => (
              <ChannelCard key={ch.channel} stats={ch} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MetricCard
// ============================================================================

function MetricCard({
  label,
  value,
  subtitle,
  accent,
}: {
  label: string;
  value: number | string;
  subtitle: string;
  accent?: boolean;
}): JSX.Element {
  return (
    <Card>
      <p className="text-xs text-text-secondary font-medium uppercase tracking-wider">{label}</p>
      <p className={`text-3xl font-heading font-bold mt-1 ${accent ? 'text-accent-brand' : 'text-text-primary'}`}>
        {typeof value === 'number' ? value.toLocaleString('ru-RU') : value}
      </p>
      <p className="text-xs text-text-secondary mt-1">{subtitle}</p>
    </Card>
  );
}

// ============================================================================
// ChannelCard
// ============================================================================

const CHANNEL_LABELS: Record<string, string> = {
  email: 'Email',
  telegram: 'Telegram',
  widget: 'Widget',
};

const CHANNEL_VARIANTS: Record<string, 'active' | 'ai' | 'waiting'> = {
  email: 'active',
  telegram: 'ai',
  widget: 'waiting',
};

function ChannelCard({ stats }: { stats: ChannelStats }): JSX.Element {
  const acceptRate = stats.proposalsGenerated > 0
    ? Math.round((stats.proposalsAccepted / stats.proposalsGenerated) * 100)
    : 0;

  return (
    <Card>
      <div className="flex items-center gap-2 mb-3">
        <Badge variant={CHANNEL_VARIANTS[stats.channel] ?? 'active'}>
          {CHANNEL_LABELS[stats.channel] ?? stats.channel}
        </Badge>
        <span className="text-xs text-text-secondary">
          {stats.activeDialogues} активных
        </span>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-xs text-text-secondary">Сообщения</p>
          <p className="text-lg font-heading font-semibold text-text-primary">
            {stats.totalMessages.toLocaleString('ru-RU')}
          </p>
        </div>
        <div>
          <p className="text-xs text-text-secondary">Proposals</p>
          <p className="text-lg font-heading font-semibold text-text-primary">
            {stats.proposalsGenerated.toLocaleString('ru-RU')}
          </p>
        </div>
        <div className="col-span-2">
          <p className="text-xs text-text-secondary">Accept rate</p>
          <div className="flex items-center gap-2 mt-1">
            <div className="flex-1 h-2 bg-surface-base rounded-full overflow-hidden">
              <div
                className="h-full bg-accent-brand rounded-full transition-all"
                style={{ width: `${acceptRate}%` }}
              />
            </div>
            <span className="text-xs font-medium text-text-primary">{acceptRate}%</span>
          </div>
        </div>
      </div>
    </Card>
  );
}

// ============================================================================
// ActivityChart (SVG bar chart)
// ============================================================================

function ActivityChart({ activity }: { activity: DailyActivity[] }): JSX.Element {
  if (!activity.length) {
    return <p className="text-sm text-text-secondary py-8 text-center">Нет данных</p>;
  }

  const maxVal = Math.max(...activity.map(d => d.proposals), 1);
  const chartH = 160;
  const barW = 100 / activity.length;

  return (
    <div>
      <svg viewBox={`0 0 100 ${chartH + 20}`} className="w-full" preserveAspectRatio="none">
        {activity.map((day, i) => {
          const x = i * barW + barW * 0.15;
          const w = barW * 0.7;
          const totalH = (day.proposals / maxVal) * chartH;
          const acceptedH = (day.accepted / maxVal) * chartH;

          return (
            <g key={day.date}>
              {/* Total proposals (background) */}
              <rect
                x={x}
                y={chartH - totalH}
                width={w}
                height={totalH}
                rx={1}
                className="fill-surface-subtle"
              />
              {/* Accepted (foreground) */}
              <rect
                x={x}
                y={chartH - acceptedH}
                width={w}
                height={acceptedH}
                rx={1}
                className="fill-accent-brand"
              />
              {/* Date label (показываем каждый 2й) */}
              {i % 2 === 0 && (
                <text
                  x={x + w / 2}
                  y={chartH + 14}
                  textAnchor="middle"
                  className="fill-text-secondary"
                  fontSize="3.5"
                >
                  {day.date.slice(5)}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      <div className="flex items-center gap-4 mt-2 justify-end">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-surface-subtle" />
          <span className="text-xs text-text-secondary">Proposals</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-accent-brand" />
          <span className="text-xs text-text-secondary">Принято</span>
        </div>
      </div>
    </div>
  );
}
